// 캐릭터 API 전송(같은 출처 /api/characters, 쿠키 자동). 동기화 전송(Transport)은 account/sync.ts 꼴 그대로 — 캐릭터 스냅샷도 같은 엔진이 맞춘다.
import { TransportError, type Transport } from '../account/sync';
import type { ConflictResponse, PutResponse, SaveResponse, Snapshot } from '../account/protocol';
import type { CharacterListResponse, CharacterResponse, CreateCharacterRequest, MigrationResponse } from './protocol';
import type { MigrationAllocation } from './allocation';

const TIMEOUT_MS = 15_000;

/** 오류 본문 {error, reason?}을 TransportError로(reason은 code 뒤에 ':'로 붙인다 — bad-name:reserved) */
async function call<T>(method: string, path: string, body?: unknown, headers: Record<string, string> = {}, keepalive = false): Promise<{ status: number; body: T }> {
  const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctl ? setTimeout(() => ctl.abort(), TIMEOUT_MS) : 0;
  let res: Response;
  try {
    res = await fetch(path, {
      method, credentials: 'same-origin', keepalive, signal: ctl?.signal ?? undefined,
      headers: body === undefined ? headers : { 'content-type': 'application/json', ...headers },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch { throw new TransportError(0, 'network'); }
  finally { if (timer) clearTimeout(timer); }
  let parsed: unknown = null;
  try { parsed = await res.json(); } catch { /* 본문 없음 */ }
  if (res.status >= 400 && res.status !== 409) {
    const p = (parsed ?? {}) as { error?: string; reason?: string; retryAfterMs?: number };
    throw new TransportError(res.status, (p.error ?? 'http-' + res.status) + (p.reason ? ':' + p.reason : ''), p.retryAfterMs);
  }
  if (res.status === 409) {
    const p = (parsed ?? {}) as { error?: string };
    // 저장 충돌(conflict)은 본문을 돌려주고, 캐릭터 규칙 거절(slots-full 등)은 오류로 던진다
    if (p.error !== 'conflict') throw new TransportError(409, p.error ?? 'http-409');
  }
  return { status: res.status, body: parsed as T };
}

/** 캐릭터 관리 API(UI·계정 클라이언트가 쓴다) */
export interface CharacterApi {
  list(): Promise<CharacterListResponse>;
  create(req: CreateCharacterRequest): Promise<CharacterResponse>;
  rename(id: string, name: string): Promise<CharacterResponse>;
  remove(id: string, confirm: string): Promise<{ deleted: true; purgeAt: number | null }>;
  restore(id: string): Promise<CharacterResponse>;
  /** 계정 이관 상태 */
  getMigration(): Promise<MigrationResponse>;
  /** 이관 시작·이어가기·넘겨받기(계정당 한 번 — 응답 mine/done) */
  claimMigration(allocation: MigrationAllocation, hash: string, claim: string): Promise<MigrationResponse>;
  /** 업로드 중 임대 갱신(소유를 잃었으면 TransportError migration-lost) */
  renewMigration(claim: string, epoch: number): Promise<MigrationResponse>;
  /** 이관 스냅샷 업로드(일반 동기화와 분리 — 펜싱 토큰 확인, 충돌 재시도 없음). 소유를 잃었으면 migration-lost */
  uploadMigrationSave(id: string, data: Snapshot, claim: string, epoch: number): Promise<{ rev: number }>;
  /** 이관 완료 */
  finishMigration(claim: string, epoch: number, hash: string): Promise<MigrationResponse>;
}

export function fetchCharacterApi(): CharacterApi {
  const enc = encodeURIComponent;
  return {
    async list() { return (await call<CharacterListResponse>('GET', '/api/characters')).body; },
    async create(req) { return (await call<CharacterResponse>('POST', '/api/characters', req)).body; },
    async rename(id, name) { return (await call<CharacterResponse>('PATCH', '/api/characters/' + enc(id), { name })).body; },
    async remove(id, confirm) { return (await call<{ deleted: true; purgeAt: number | null }>('DELETE', '/api/characters/' + enc(id), { confirm })).body; },
    async restore(id) { return (await call<CharacterResponse>('POST', '/api/characters/' + enc(id) + '/restore', {})).body; },
    async getMigration() { return (await call<MigrationResponse>('GET', '/api/characters/migration')).body; },
    async claimMigration(allocation, hash, claim) { return (await call<MigrationResponse>('POST', '/api/characters/migration', { allocation, hash, claim })).body; },
    async renewMigration(claim, epoch) { return (await call<MigrationResponse>('POST', '/api/characters/migration/lease', { claim, epoch })).body; },
    async uploadMigrationSave(id, data, claim, epoch) { return (await call<{ rev: number }>('PUT', '/api/characters/' + enc(id) + '/migration-save', { data, claim, epoch })).body; },
    async finishMigration(claim, epoch, hash) { return (await call<MigrationResponse>('POST', '/api/characters/migration/done', { claim, epoch, hash })).body; },
  };
}

/** 캐릭터 스냅샷 전송(account/sync.ts Transport). keepalive = 페이지가 숨겨질 때 마지막 올리기 */
export function characterTransport(id: string, keepalive = () => false): Transport {
  const base = '/api/characters/' + encodeURIComponent(id) + '/save';
  return {
    async getSave(metaOnly = false) { return (await call<SaveResponse>('GET', metaOnly ? base + '?meta=1' : base)).body; },
    async putSave(rev: number, data: Snapshot, overwrite = false) {
      const body = { data };
      const ka = keepalive() && JSON.stringify(body).length < 60_000;
      const headers: Record<string, string> = { 'if-match': `"${rev}"` };
      if (overwrite) headers['x-dh-sync'] = 'overwrite';
      const r = await call<PutResponse | ConflictResponse>('PUT', base, body, headers, ka);
      return r.status === 409 ? { ok: false as const, conflict: r.body as ConflictResponse } : { ok: true as const, res: r.body as PutResponse };
    },
  };
}
